import { gql } from "@apollo/client";
import JsonViewScreen from "~/components/debug/JsonViewScreen";
import { usePermissionOrRedirect, Permission } from "~/hooks/permissions";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Modal,
  CircularProgress,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useOptimizerLogIndexViewQuery, useOptimizerLogInitialStateQuery } from "~/graphql/__generated__/graphql";

type OptimizerLog = {
  id: number;
  createdAt: string;
  logData: string;
  createdBy: {
    email: string;
  } | null;
};


export function OptimizerLogIndexView() {
  usePermissionOrRedirect(Permission.debugApps, "/command");

  const { id: idParam } = useParams<{ id?: string }>();
  const urlLogId = idParam ? parseInt(idParam, 10) : null;

  const LIMIT = 10;
  const [selectedLog, setSelectedLog] = useState<OptimizerLog | null>(null);
  const [page, setPage] = useState(0);
  const [pageInput, setPageInput] = useState("");

  const [optimizerLogIdInput, setOptimizerLogIdInput] = useState("");
  const [optimizerLogIdFilter, setOptimizerLogIdFilter] = useState<number | null>(null);

  const offset = page * LIMIT;

  const { data, loading } = useOptimizerLogIndexViewQuery({
    variables: {
      limit: LIMIT,
      offset,
      where: optimizerLogIdFilter ? { id: { eq: optimizerLogIdFilter } } as never : null,
    },
    fetchPolicy: "network-only",
  });

  const autoOpened = useRef(false);
  useEffect(() => {
    if (!urlLogId || loading || autoOpened.current) return;
    const match = data?.optimizerLog?.find((log) => log.id === urlLogId);
    if (match) {
      autoOpened.current = true;
      setSelectedLog(match);
    }
  }, [urlLogId, loading, data]);

  const logs = data?.optimizerLog || [];

  const { data: initialStateData, loading: initialStateLoading } = useOptimizerLogInitialStateQuery({
    variables: {
      optimizerLogId: selectedLog?.id ?? 0,
    },
    skip: !selectedLog,
  });

  const handlePrev = () => setPage((p) => Math.max(0, p - 1));
  const handleNext = () => setPage((p) => p + 1);

  const handlePageInputSubmit = () => {
    const num = parseInt(pageInput);
    if (!isNaN(num) && num > 0) {
      setPage(num - 1);
    }
    setPageInput("");
  };

  const handleFilterSubmit = () => {
    const parsedId = parseInt(optimizerLogIdInput, 10);
    setOptimizerLogIdFilter(Number.isNaN(parsedId) ? null : parsedId);
  };

  useEffect(() => {
    setPage(0);
    setPageInput("");
  }, [optimizerLogIdFilter]);

  const handleOpenLog = (log: OptimizerLog) => setSelectedLog(log);
  const handleCloseLog = () => setSelectedLog(null);

  return (
    <Box sx={{ height: "100%", width: "100%", mt: 4, mb: 4 }}>
      <Grid container spacing={2}>
        <Grid component="div">
          <Paper sx={{ width: "100%", p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Recent Optimizer Logs
              </Typography>

              <Box mb={2} display="flex" gap={1} alignItems="center">
                <TextField
                  size="small"
                  label="OptimizerLog ID"
                  value={optimizerLogIdInput}
                  onChange={(e) => setOptimizerLogIdInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleFilterSubmit();
                  }}
                />
                <Button variant="contained" size="small" onClick={handleFilterSubmit}>
                  Filter
                </Button>
                {optimizerLogIdFilter !== null && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setOptimizerLogIdInput("");
                      setOptimizerLogIdFilter(null);
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>User Email</TableCell>
                    <TableCell>OptimizerLog ID</TableCell>
                    <TableCell>View</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: "center" }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: "center" }}>
                        No logs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>{new Date(log.createdAt).toLocaleString()}</TableCell>
                        <TableCell>{log.createdBy?.email || "Unknown"}</TableCell>
                        <TableCell>{log.id}</TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => handleOpenLog(log)}>
                            View JSON
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Box mt={2} display="flex" alignItems="center" gap={1}>
                <Button variant="outlined" onClick={handlePrev} disabled={page === 0}>
                  Previous
                </Button>
                <Typography ml={0}>
                  Page {page + 1} {loading && "(loading...)"}
                </Typography>
                <Button variant="outlined" onClick={handleNext} disabled={logs.length < LIMIT}>
                  Next
                </Button>
              </Box>
              <Box mt={2} display="flex" alignItems="center" gap={1}>
                <TextField
                  size="small"
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handlePageInputSubmit();
                  }}
                  sx={{ width: 60 }}
                />
                <Button variant="outlined" onClick={handlePageInputSubmit}>
                  Jump to page
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Modal
        open={!!selectedLog}
        onClose={handleCloseLog}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            width: "90%",
            maxHeight: "90%",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 3,
            overflowY: "auto",
          }}
        >
          {selectedLog && (
            <JsonViewScreen
              title={`Log ${selectedLog.id}`}
              subtitle={`Created: ${new Date(selectedLog.createdAt)} by ${selectedLog.createdBy?.email || "Unknown"}`}
              data={selectedLog.logData || "{}"}
              loading={false}
            />
          )}
          {initialStateData && (
            <JsonViewScreen
              title={`Initial State for Log ${selectedLog?.id}`}
              subtitle={""}
              data={initialStateData.optimizerLogInitialState || "{}"}
              loading={initialStateLoading}
            />
          )}
          <Box sx={{ textAlign: "right", mt: 2 }}>
            <Button onClick={handleCloseLog}>Close</Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

const OPTIMIZER_LOG_INDEX_VIEW_QUERY = gql`
  query OptimizerLogIndexView($limit: Int, $offset: Int, $where: OptimizerLogFilters) @permission(requires: debugApps) {
    optimizerLog(limit: $limit, offset: $offset, orderBy: { id: { direction: desc, priority: 1 } }, where: $where) {
      id
      createdAt
      logData
      createdBy {
        email
      }
    }
  }
`;

const OPTIMIZER_LOG_INITIAL_STATE_QUERY = gql`
  query OptimizerLogInitialState($optimizerLogId: Int!) @permission(requires: debugApps) {
    optimizerLogInitialState(optimizerLogId: $optimizerLogId)
  }
`;